<template>
  <div id="bulkTablePrices">
    <LabelDefault>Pricing Tiers</LabelDefault>
    <div class="flex flex-wrap flex-row space-x-4 mb-5">
      <span
        v-for="(price, idx) in uniquePrices"
        :key="idx"
        :id="`price-filter-${price}`"
        :class="[
          'price cursor-pointer',
          modelValue === price ? 'font-bold text-fg-primary' : 'font-normal text-fg-link-primary',
          isUpdatingTablePricesInBulk ? 'opacity-50 cursor-not-allowed' : '',
        ]"
        @click="setPriceToFilter(price)"
      >
        ${{ price }}
      </span>
    </div>
    <LabelDefault>Set New Price</LabelDefault>
    <div class="flex space-x-3 items-stretch">
      <NumberInput
        :modelValue="newPrice || 0"
        @update:modelValue="newPrice = $event"
        currency
        classes="w-20"
        min="0"
        step="1"
        inputClasses="py-1"
        id="new-price-input"
        :disabled="isUpdatingTablePricesInBulk"
      />
      <ButtonDefault
        v-if="!hideCancel"
        id="cancel-button"
        label="Cancel"
        alt
        @click="onCancel()"
        :disabled="isUpdatingTablePricesInBulk"
      />
      <ButtonDefault
        id="save-button"
        :label="submitLabel"
        @click="showModalConfirmation()"
        :disabled="isUpdatingTablePricesInBulk || modelValue === null"
      />
    </div>
    <SlotModal id="confirm-update-price-in-bulk-modal" :display="showConfirmationModal">
      <template #header>Please Confirm</template>
      <div class="mb-8">
        There is already a group of tables with this price. Are you sure you want to combine these
        two groups into one?
      </div>
      <div class="flex items-center justify-between">
        <ButtonDefault id="cancel-in-bulk" label="CANCEL" alt @click="onCancel()" />
        <ButtonDefault id="save-in-bulk" :label="submitLabel" @click="onSave()" />
      </div>
    </SlotModal>
  </div>
</template>

<script lang="ts">
import { PropType, defineComponent } from 'vue'

import ButtonDefault from '@/common/components/atoms/ButtonDefault.vue'
import LabelDefault from '@/common/components/atoms/LabelDefault.vue'
import NumberInput from '@/common/components/molecules/NumberInput.vue'
import SlotModal from '@/common/components/organisms/SlotModal.vue'

export default defineComponent({
  name: 'BulkTablePrices',
  components: { LabelDefault, SlotModal, NumberInput, ButtonDefault },
  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },
  props: {
    modelValue: {
      type: Number as PropType<number | null>,
      default: null,
    },
    uniquePrices: {
      type: Array as PropType<number[]>,
      default: null,
    },
    isUpdatingTablePricesInBulk: {
      type: Boolean,
      default: false,
    },
    hideCancel: {
      type: Boolean,
      default: false,
    },
    submitLabel: {
      type: String,
      default: 'Save',
    },
  },
  emits: {
    cancel: () => true,
    // eslint-disable-next-line no-unused-vars
    updatePrice: async (newPrice: number) => true,
    // eslint-disable-next-line no-unused-vars
    'update:modelValue': (price: number) => true,
  },
  data() {
    return {
      newPrice: null as number | null,
      showConfirmationModal: false,
    }
  },
  mounted() {
    this.newPrice = this.modelValue
  },
  methods: {
    setPriceToFilter(price: number) {
      if (this.isUpdatingTablePricesInBulk) {
        return
      }
      this.newPrice = price
      this.$emit('update:modelValue', price)
    },
    onCancel() {
      if (this.isUpdatingTablePricesInBulk) {
        return
      }
      this.showConfirmationModal = false
      this.$emit('cancel')
    },
    async showModalConfirmation() {
      if (this.newPrice === this.modelValue) {
        this.onCancel()
      } else if (this.isNewPriceAlreadyInTierGroup) {
        this.showConfirmationModal = true
      } else {
        await this.onSave()
      }
    },
    async onSave() {
      if (this.isUpdatingTablePricesInBulk) {
        return
      }
      this.showConfirmationModal = false
      if (this.newPrice !== null) {
        await this.$emit('updatePrice', this.newPrice)
      }
    },
  },
  computed: {
    isNewPriceAlreadyInTierGroup(): boolean {
      return this.newPrice !== null ? this.uniquePrices.includes(this.newPrice) : false
    },
  },
})
</script>
